
export interface Device {
    id: string;
    accountId: string;
    owner?: string;
    serialNumber?: string;
    token?: string;
    type?: DeviceType;
    model?: string;
    status: DeviceStatus;
    disabledForSchool?: boolean;
    activationDatetime?: Date;
    creationDatetime: Date; 
    lastModifiedDatetime: Date;
    enrolmentDatetime?: Date;
    registrationRequestCount?: number;
    registrationRequestedDatetime?: Date;
    /**
     * Set when the backend's heartbeat sweep has warned or alerted about this device not checking
     * in, and cleared by the device's next check-in. Read-only: the school cannot change it, it is
     * there so a silent device is visible rather than looking identical to a healthy one.
     */
    staleAlertSeverity?: StaleAlertSeverity;
    /** The device's last check-in when that escalation fired — i.e. silent since. */
    staleSince?: Date;
}

/**
 * When a pupil's device last checked in with LOQT (DIGI-1700). Devices that never reported have
 * no entry at all, which is how the device list tells "Never checked in" from a late one.
 */
export interface DeviceCheckIn {
    accountId: string;
    serialNumber: string;
    lastHeartbeatAt: Date;
}

/** Mirrors StaleAlertSeverity on the backend. */
export enum StaleAlertSeverity {
    /** Not checking in; the parents have been pushed. */
    WARN = "WARN",
    /** Still not checking in well past the warning; parents pushed and texted. */
    ALERT = "ALERT"
}

export enum DeviceType {
    IOS = "IOS",
    ANDROID = "ANDROID",
    UNKNOWN = "UNKNOWN"
}

export enum DeviceStatus {
    INACTIVE = "INACTIVE",
    ENROLLED = "ENROLLED",
    PENDING_ENROLLMENT = "PENDING_ENROLLMENT",
    UNENROLLED = "UNENROLLED"
}

/** What staff read for each status, worded like the help page (DIGI-1878: UK "enrolment"). */
export const DEVICE_STATUS_LABELS: Record<DeviceStatus, string> = {
    [DeviceStatus.INACTIVE]: "Inactive",
    [DeviceStatus.ENROLLED]: "Enrolled",
    [DeviceStatus.PENDING_ENROLLMENT]: "Pending enrolment",
    [DeviceStatus.UNENROLLED]: "Unenrolled"
};

/** Display only: the status value itself stays the API value. An unknown status passes through. */
export function deviceStatusLabel(status: string | null | undefined): string {
    if (!status) {
        return "";
    }
    return DEVICE_STATUS_LABELS[status as DeviceStatus] || status;
}